import { delay, exists } from '@jack-henry/frontend-utils/functions';
import {
    DynamicComponentExitReason,
    DynamicComponentHandle,
    DynamicComponentRegistry,
    DynamicComponentResult,
} from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import type { LitElement } from 'lit';
import {
    IDialogService,
    normalizeConfig,
    TmDialogContent,
    TmDialogServiceConfig,
    TmDialogServiceConfigFull,
} from './tm-dialog.service.types';
/**
 * Base class for all dialog service flavors.
 *
 * Primarily exists to enable a sub-class DI provider so
 * that the same DI token can be used in both legacy and modern apps.
 *
 * TODO: Consolidate with `ModernDialogService` when `OmegaDialogService` is decommissioned.
 */
export abstract class TmDialogService<
    DialogElement extends LitElement = LitElement,
> implements IDialogService<DialogElement> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    protected handle?: DynamicComponentHandle<DialogElement, any>;

    /**
     * Tracks state about any currently open dialogs.
     *
     * At present, the service only supports modal dialogs (i.e., one at a time).
     * The registry supports multiple dialogs, but an appropriate UX has not been decided.
     */
    protected readonly registry = new DynamicComponentRegistry<DialogElement>(false);

    /**
     * Consumer-provided configuration applied to all dialogs opened by this service
     * unless overridden by the `open()` method's `options` parameter.
     */
    private defaultConfig?: Partial<TmDialogServiceConfig>;

    public abstract open<T>(
        content: TmDialogContent<T>,
        title: string,
        options?: TmDialogServiceConfig<T>
    ): Promise<DynamicComponentResult<T>>;

    /**
     * Close the open dialog with a reason and optional data
     * for it return from its `open()` promise.
     *
     * @param data If specified, takes precedence over any data set using
     * the `setData()` callback passed to the `open()` method's `content`
     * parameter callback.
     */
    public async close<T>(reason = DynamicComponentExitReason.ForceClose, data?: T) {
        if (!this.handle) {
            return;
        }
        const { dispose, disposed } = this.handle;

        dispose(
            {
                reason,
                data,
            },
            delay(300)
        );

        this.handle = undefined;
        await disposed;
    }

    public closeAll() {
        this.registry.destroyAll();
    }

    public get isDialogOpen() {
        return exists(this.handle);
    }

    public setDefaultConfiguration(config: Partial<TmDialogServiceConfig>) {
        this.defaultConfig = config;
    }

    public getDialogElement() {
        if (!this.handle) {
            throw new Error('Cannot get dialog element; there is no open dialog');
        }

        return this.handle.element;
    }

    public abstract getContent<E extends Element = Element>(): E;

    /**
     * Normalize a partial configuration into a fully-filled configuration.
     */
    protected normalizeConfig<T>(config?: TmDialogServiceConfig<T>): TmDialogServiceConfigFull<T> {
        const combined = {
            ...(this.defaultConfig as TmDialogServiceConfig<T>),
            ...config,
        };

        return normalizeConfig(combined);
    }
}
